import { FEEDBACK_STORAGE_KEYS } from './feedbackTypes.js';

// v3 (Round 3, R3-H01): snapshots carry an explicit activationState and the
// origin-wide identityEpoch they were written under, so an actor boundary can
// deactivate work without deleting it and a stale tab can never resurrect it.
export const FEEDBACK_SESSION_SCHEMA_VERSION = 3;
const V2_FEEDBACK_SESSION_SCHEMA_VERSION = 2;
export const LEGACY_FEEDBACK_SESSION_STORAGE_KEY = FEEDBACK_STORAGE_KEYS.session;
// Internal IDs are opaque identifiers, not emails/display names/persona labels.
// Existing test and seed identities include both `USR-*` and `user-*` shapes.
const ACTOR_ID_PATTERN = /^[A-Za-z][A-Za-z0-9_-]{1,127}$/;

function getStorage(storage) {
  if (storage) return storage;
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}

function isActorId(value) {
  return ACTOR_ID_PATTERN.test(String(value || ''));
}

function parseSnapshot(raw) {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
  } catch {
    return null;
  }
}

function snapshotSavedAt(snapshot) {
  const time = Date.parse(snapshot?.savedAt || '');
  return Number.isFinite(time) ? time : Number.NEGATIVE_INFINITY;
}

function legacyOwner(snapshot) {
  const owner = snapshot?.feedbackPackage?.createdBy?.id;
  return isActorId(owner) ? String(owner) : null;
}

export function feedbackSessionStorageKey(ownerUserId) {
  if (!isActorId(ownerUserId)) return null;
  return `${LEGACY_FEEDBACK_SESSION_STORAGE_KEY}:v3:${ownerUserId}`;
}

function feedbackSessionStorageKeyV2(ownerUserId) {
  if (!isActorId(ownerUserId)) return null;
  return `${LEGACY_FEEDBACK_SESSION_STORAGE_KEY}:v2:${ownerUserId}`;
}

export function createFeedbackSessionSnapshot({
  ownerUserId,
  packageId,
  feedbackPackage,
  activeStage,
  selectedAnnotationId,
  resumeContext,
  currentRoute,
  saveStatus,
  saveMessage,
  pendingMutations,
  activationState,
  identityEpoch,
}) {
  const normalizedActivationState = activationState === 'inactive' ? 'inactive' : 'active';
  return {
    schemaVersion: FEEDBACK_SESSION_SCHEMA_VERSION,
    activationState: normalizedActivationState,
    // Kept in sync with activationState so existing inactive-session logic
    // (explicit resume after "keep my work") reads one truth.
    sessionInactive: normalizedActivationState !== 'active',
    identityEpoch: Number.isInteger(Number(identityEpoch)) && Number(identityEpoch) > 0
      ? Number(identityEpoch)
      : null,
    ownerUserId: isActorId(ownerUserId || feedbackPackage?.createdBy?.id)
      ? ownerUserId || feedbackPackage?.createdBy?.id
      : null,
    packageId: packageId || feedbackPackage?.id || null,
    feedbackPackage: feedbackPackage || null,
    activeStageScreenId:
      activeStage?.screenId || activeStage?.metadata?.screenId || activeStage?.id || null,
    activeStage: activeStage || null,
    selectedAnnotationId: selectedAnnotationId || null,
    resumeContext: resumeContext || null,
    currentRoute: currentRoute || null,
    saveStatus: saveStatus || '',
    saveMessage: saveMessage || '',
    lastAutosaveAt: activeStage?.lastAutosaveAt || null,
    lastServerSaveAt: activeStage?.lastServerSaveAt || null,
    savedAt: new Date().toISOString(),
    pendingMutations: pendingMutations && typeof pendingMutations === 'object' ? pendingMutations : {},
  };
}

function snapshotScreenId(screen, index) {
  return screen?.screenId || screen?.metadata?.screenId || screen?.id || `screen-${index}`;
}

// A package id proves only that the package exists; it does not prove every
// capture reached the server. A screen is recoverable when this snapshot still
// carries its raw screenshot or the server assigned that capture a durable id.
// Package and active-stage copies are grouped because they can represent the
// same capture at different edit revisions.
export function feedbackSnapshotIsRestorable(snapshot) {
  const screens = [
    ...(snapshot?.feedbackPackage?.screens || []),
    ...(snapshot?.activeStage ? [snapshot.activeStage] : []),
  ];
  const restorableByScreen = new Map();
  screens.forEach((screen, index) => {
    if (!screen) return;
    const screenId = snapshotScreenId(screen, index);
    const restorable = Boolean(screen.screenshotDataUrl || screen.persistedCaptureId);
    restorableByScreen.set(screenId, Boolean(restorableByScreen.get(screenId) || restorable));
  });
  return [...restorableByScreen.values()].every(Boolean);
}

// Default local packages are titled `Feedback package YYYY-MM-DD HH:MM` with
// empty instructions — anything else is user-authored.
const DEFAULT_PACKAGE_TITLE_PATTERN = /^Feedback package \d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;

function screenAnnotations(screen) {
  return screen?.metadata?.annotations || screen?.annotations || [];
}

// Pure meaningful-work classifier (Round 3, INV-11): a session is meaningful
// when losing it would lose tester work or unresolved state. A default package
// with no screen, stage, user edit, failure, or pending mutation is empty.
export function feedbackSnapshotHasMeaningfulWork(snapshot) {
  if (!snapshot) return false;
  const pkg = snapshot.feedbackPackage;
  const screens = pkg?.screens || [];
  if (screens.length > 0) return true;
  if (snapshot.activeStage) return true;
  const annotated = [snapshot.activeStage, ...screens].some(
    (screen) => screenAnnotations(screen).length > 0,
  );
  if (annotated) return true;
  if (pkg?.title && !DEFAULT_PACKAGE_TITLE_PATTERN.test(String(pkg.title))) return true;
  if (String(pkg?.overallInstructions || '').trim()) return true;
  if (snapshot.saveStatus === 'failed' || snapshot.saveStatus === 'conflict') return true;
  if (pkg?.recoveryKind || snapshot.snapshotRecoveryKind === 'non-restorable-trimmed') return true;
  if (Object.keys(snapshot.pendingMutations || {}).length > 0) return true;
  return false;
}

// Screenshots dominate snapshot size; multi-screen sessions can exceed the
// ~5MB localStorage quota. The trimmed form keeps identity, marks, and notes
// only when every stripped screenshot has a durable server capture.
function trimHeavySnapshotFields(snapshot) {
  const strip = (screen) =>
    screen ? { ...screen, screenshotDataUrl: null, annotatedSvg: null } : screen;
  const trimmedActiveStage = strip(snapshot.activeStage);
  const trimmedFeedbackPackage = snapshot.feedbackPackage
    ? {
        ...snapshot.feedbackPackage,
        screens: (snapshot.feedbackPackage.screens || []).map(strip),
      }
    : snapshot.feedbackPackage;
  const fullyServerRecoverable = feedbackSnapshotIsRestorable({
    ...snapshot,
    activeStage: trimmedActiveStage,
    feedbackPackage: trimmedFeedbackPackage,
  });
  const recoveryKind = fullyServerRecoverable
    ? 'server-recoverable-trimmed'
    : 'non-restorable-trimmed';
  return {
    ...snapshot,
    snapshotTrimmed: true,
    snapshotRecoveryKind: recoveryKind,
    activeStage: trimmedActiveStage,
    feedbackPackage: trimmedFeedbackPackage
      ? {
          ...trimmedFeedbackPackage,
          recoveryKind,
        }
      : trimmedFeedbackPackage,
  };
}

const FRIENDLY_STORAGE_LIMIT_MESSAGE =
  'This draft is too large for browser recovery storage. Saved drafts stay safe on the server; local crash recovery will restore your marks and notes but not the raw screenshots.';
const LOCAL_ONLY_STORAGE_LIMIT_MESSAGE =
  'Browser recovery storage is full. At least one screenshot exists only on this device, so feedback mode must stay open until it is saved to the server.';

export function writeFeedbackSessionSnapshot(snapshot, storage, ownerUserId = snapshot?.ownerUserId) {
  const target = getStorage(storage);
  const key = feedbackSessionStorageKey(ownerUserId);
  if (!target || !key) return { ok: false, error: 'Feedback recovery is unavailable for this session.' };
  if (snapshot?.ownerUserId !== ownerUserId) {
    return { ok: false, error: 'Feedback recovery owner validation failed.' };
  }
  try {
    target.setItem(key, JSON.stringify({ ...snapshot, savedAt: new Date().toISOString() }));
    return { ok: true };
  } catch {
    // Quota exceeded (or storage hiccup): degrade to the trimmed snapshot
    // instead of losing recovery entirely — and never surface the raw
    // browser exception text to the tester.
    try {
      const trimmed = { ...trimHeavySnapshotFields(snapshot), savedAt: new Date().toISOString() };
      target.setItem(key, JSON.stringify(trimmed));
      const restorable = trimmed.snapshotRecoveryKind === 'server-recoverable-trimmed';
      return {
        ok: true,
        degraded: true,
        restorable,
        recoveryKind: trimmed.snapshotRecoveryKind,
        warning: restorable ? FRIENDLY_STORAGE_LIMIT_MESSAGE : LOCAL_ONLY_STORAGE_LIMIT_MESSAGE,
      };
    } catch {
      return { ok: false, error: FRIENDLY_STORAGE_LIMIT_MESSAGE };
    }
  }
}

export function readFeedbackSessionSnapshot(storage, ownerUserId) {
  const target = getStorage(storage);
  const key = feedbackSessionStorageKey(ownerUserId);
  if (!target || !key) return null;
  const raw = target.getItem(key);
  const snapshot = parseSnapshot(raw);
  if (
    !snapshot ||
    snapshot.schemaVersion !== FEEDBACK_SESSION_SCHEMA_VERSION ||
    snapshot.ownerUserId !== ownerUserId
  ) {
    if (raw) {
      try {
        target.removeItem(key);
      } catch {
        /* unavailable browser storage is non-fatal */
      }
    }
    return null;
  }
  return snapshot;
}

export function migrateLegacyFeedbackSessionSnapshot(storage) {
  const target = getStorage(storage);
  if (!target) return { migrated: false, ownerUserId: null };
  const raw = target.getItem(LEGACY_FEEDBACK_SESSION_STORAGE_KEY);
  if (!raw) return { migrated: false, ownerUserId: null };
  const legacy = parseSnapshot(raw);
  const ownerUserId = legacyOwner(legacy);
  if (!legacy || !ownerUserId) {
    try {
      target.removeItem(LEGACY_FEEDBACK_SESSION_STORAGE_KEY);
    } catch {
      /* unavailable browser storage is non-fatal */
    }
    return { migrated: false, ownerUserId: null, unsafe: true };
  }
  const key = feedbackSessionStorageKey(ownerUserId);
  const parsedExisting = parseSnapshot(target.getItem(key));
  const existing =
    parsedExisting?.schemaVersion === FEEDBACK_SESSION_SCHEMA_VERSION &&
    parsedExisting?.ownerUserId === ownerUserId
      ? parsedExisting
      : null;
  const legacySavedAt = [legacy.savedAt, legacy.updatedAt, legacy.createdAt].find((value) =>
    Number.isFinite(Date.parse(value || '')),
  );
  // Round 3 (spec H5): legacy snapshots migrate INACTIVE — preserved work must
  // never silently reactivate — and empty or submitted ones are dropped.
  const submitted =
    legacy.feedbackPackage?.status === 'submitted' || Boolean(legacy.feedbackPackage?.submittedAt);
  if (submitted || !feedbackSnapshotHasMeaningfulWork(legacy)) {
    try {
      target.removeItem(LEGACY_FEEDBACK_SESSION_STORAGE_KEY);
    } catch {
      /* unavailable browser storage is non-fatal */
    }
    return { migrated: false, ownerUserId, removed: true };
  }
  const migrated = {
    ...createFeedbackSessionSnapshot({
      ...legacy,
      ownerUserId,
      feedbackPackage: legacy.feedbackPackage,
      activeStage: legacy.activeStage,
      activationState: 'inactive',
    }),
    // Do not let the migration timestamp make an older global snapshot look
    // newer than a versioned snapshot that was already written by its owner.
    ...(legacySavedAt ? { savedAt: legacySavedAt } : {}),
  };
  try {
    if (!existing || (legacySavedAt && snapshotSavedAt(migrated) > snapshotSavedAt(existing))) {
      target.setItem(key, JSON.stringify(migrated));
    }
    target.removeItem(LEGACY_FEEDBACK_SESSION_STORAGE_KEY);
    return { migrated: true, ownerUserId };
  } catch {
    return { migrated: false, ownerUserId };
  }
}

// v2 -> v3 migration (Round 3, spec H5): empty v2 snapshots are removed;
// meaningful ones migrate INACTIVE (work preserved, no surprise activation);
// submitted snapshots are never made resumable. Owner-validated, idempotent.
export function migrateFeedbackSessionSnapshotToV3(storage, ownerUserId) {
  const target = getStorage(storage);
  const v2Key = feedbackSessionStorageKeyV2(ownerUserId);
  if (!target || !v2Key) return { migrated: false, removed: false };
  const raw = target.getItem(v2Key);
  if (!raw) return { migrated: false, removed: false };
  const removeV2 = () => {
    try {
      target.removeItem(v2Key);
    } catch {
      /* unavailable browser storage is non-fatal */
    }
  };
  const legacy = parseSnapshot(raw);
  if (
    !legacy ||
    legacy.schemaVersion !== V2_FEEDBACK_SESSION_SCHEMA_VERSION ||
    legacy.ownerUserId !== ownerUserId
  ) {
    removeV2();
    return { migrated: false, removed: true };
  }
  const submitted =
    legacy.feedbackPackage?.status === 'submitted' || Boolean(legacy.feedbackPackage?.submittedAt);
  if (submitted || !feedbackSnapshotHasMeaningfulWork(legacy)) {
    removeV2();
    return { migrated: false, removed: true };
  }
  const v3Key = feedbackSessionStorageKey(ownerUserId);
  const existing = parseSnapshot(target.getItem(v3Key));
  const migrated = {
    ...legacy,
    schemaVersion: FEEDBACK_SESSION_SCHEMA_VERSION,
    activationState: 'inactive',
    sessionInactive: true,
    identityEpoch: null,
  };
  try {
    if (existing?.schemaVersion !== FEEDBACK_SESSION_SCHEMA_VERSION) {
      target.setItem(v3Key, JSON.stringify(migrated));
    }
    removeV2();
    return { migrated: existing?.schemaVersion !== FEEDBACK_SESSION_SCHEMA_VERSION, removed: false };
  } catch {
    return { migrated: false, removed: false };
  }
}

export function clearFeedbackSessionSnapshot(storage, ownerUserId) {
  const target = getStorage(storage);
  const key = feedbackSessionStorageKey(ownerUserId);
  if (!target || !key) return;
  try {
    target.removeItem(key);
  } catch {
    /* ignore private-mode storage failures */
  }
}
